import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { assertOnlineOrThrow } from '../lib/offlineGuard';
import { mergeFeatureFlagMaps, buildDefaultFlagMap } from '../lib/featureFlags';
import { cacheGetOrSet, cacheDelete, CACHE_TTL } from '../lib/cache';

/**
 * @returns {Promise<Record<string, { is_enabled: boolean, launch_at: string | null }>>}
 */
export async function fetchFeatureFlagsFromDb() {
  return cacheGetOrSet('feature_flags', CACHE_TTL.FEATURE_FLAGS, async () => {
    if (!isSupabaseConfigured) return buildDefaultFlagMap();
    const sb = getSupabase();
    if (!sb) return buildDefaultFlagMap();
    try {
      assertOnlineOrThrow();
    } catch {
      return buildDefaultFlagMap();
    }
    const { data, error } = await sb.from('feature_flags').select('feature,is_enabled,launch_at');
    if (error || !Array.isArray(data)) return buildDefaultFlagMap();
    /** @type {Record<string, { is_enabled: boolean, launch_at: string | null }>} */
    const raw = {};
    for (const row of data) {
      const k = row.feature;
      if (!k) continue;
      raw[k] = {
        is_enabled: !!row.is_enabled,
        launch_at: row.launch_at ?? null,
      };
    }
    return mergeFeatureFlagMaps(raw);
  });
}

/**
 * @param {{ featureKey: string, email?: string | null, locale?: string | null }} p
 */
export async function insertFeatureWaitlist(p) {
  if (!isSupabaseConfigured) return { ok: false, reason: 'no_sb' };
  const sb = getSupabase();
  if (!sb) return { ok: false, reason: 'no_sb' };
  try {
    assertOnlineOrThrow();
  } catch {
    return { ok: false, reason: 'offline' };
  }
  let userId = null;
  try {
    const {
      data: { user },
    } = await sb.auth.getUser();
    if (user) {
      const { data: urow } = await sb.from('users').select('id').eq('auth_id', user.id).maybeSingle();
      userId = urow?.id ?? null;
    }
  } catch {
    userId = null;
  }
  const { error } = await sb.from('feature_waitlist').insert({
    feature_key: p.featureKey,
    user_id: userId,
    email: p.email || null,
    locale: p.locale || null,
  });
  if (error) return { ok: false, reason: error.message };
  return { ok: true };
}

/**
 * @param {Array<{ feature?: string, feature_key?: string, is_enabled: boolean, launch_at?: string | null }>} rows
 */
export async function upsertFeatureFlagsRows(rows) {
  if (!isSupabaseConfigured) return { ok: false, error: 'NO_SB' };
  const sb = getSupabase();
  if (!sb) return { ok: false, error: 'NO_SB' };
  const { error } = await sb.from('feature_flags').upsert(
    rows.map((r) => ({
      feature: r.feature ?? r.feature_key,
      is_enabled: !!r.is_enabled,
      launch_at: r.launch_at || null,
      updated_at: new Date().toISOString(),
    })),
    { onConflict: 'feature' },
  );
  if (error) return { ok: false, error: error.message };
  cacheDelete('feature_flags');
  return { ok: true };
}
